import React, {useEffect, useRef} from 'react';
import {Col, Row} from 'react-bootstrap';
import {DateTime} from 'luxon';
import echarts from '@/utils/echartsCore';
import i18n from 'i18n';

export interface StoryboardAccessPieSlice {
    label: string;
    value: number;
    storyboardHashId: string | null;
    isOther?: boolean;
}

export interface StoryboardAccessTimelineSeries {
    storyboardHashId: string | null;
    label: string;
    data: number[];
    isOther?: boolean;
}

export interface UserAuditStoryboardAccessChartsProps {
    pieSlices: StoryboardAccessPieSlice[];
    dayKeys: string[];
    timelineSeries: StoryboardAccessTimelineSeries[];
}

const CHART_HEIGHT_PX = 280;
const TARGET_AXIS_LABELS = 12;

function tooltipDayTitle(dayKey: string): string {
    try {
        return DateTime.fromISO(dayKey, {zone: 'utc'}).toLocaleString(DateTime.DATE_MED);
    } catch {
        return dayKey;
    }
}

function pieSliceName(s: StoryboardAccessPieSlice): string {
    if (s.isOther) {
        return i18n.t('ub_chart_storyboard_other.text');
    }
    const base = (s.label ?? '').trim() || s.storyboardHashId || '—';
    return base.length > 40 ? `${base.slice(0, 40)}…` : base;
}

function timelineSeriesName(s: StoryboardAccessTimelineSeries): string {
    if (s.isOther) {
        return i18n.t('ub_chart_storyboard_other.text');
    }
    const base = (s.label ?? '').trim() || s.storyboardHashId || '—';
    return base.length > 28 ? `${base.slice(0, 28)}…` : base;
}

const UserAuditStoryboardAccessCharts: React.FC<UserAuditStoryboardAccessChartsProps> = ({
    pieSlices,
    dayKeys,
    timelineSeries,
}) => {
    const pieRef = useRef<HTMLDivElement>(null);
    const lineRef = useRef<HTMLDivElement>(null);

    const showPie = pieSlices.length > 0;
    const showLine = timelineSeries.length > 0 && dayKeys.length > 0;

    useEffect(() => {
        const el = pieRef.current;
        if (!el || !showPie) {
            return;
        }
        const chart = echarts.init(el);
        const handleResize = () => {
            if (!chart.isDisposed()) {
                chart.resize();
            }
        };
        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(el);
        }
        window.addEventListener('resize', handleResize);

        chart.setOption(
            {
                textStyle: {fontFamily: '"Rubik", sans-serif', fontSize: 12},
                tooltip: {trigger: 'item' as const},
                series: [
                    {
                        type: 'pie' as const,
                        radius: ['32%', '72%'],
                        avoidLabelOverlap: true,
                        data: pieSlices.map((s) => ({
                            name: pieSliceName(s),
                            value: s.value,
                        })),
                    },
                ],
            },
            true,
        );

        return () => {
            window.removeEventListener('resize', handleResize);
            if (resizeObserver && el) {
                resizeObserver.unobserve(el);
            }
            chart.dispose();
        };
    }, [pieSlices, showPie]);

    useEffect(() => {
        const el = lineRef.current;
        if (!el || !showLine) {
            return;
        }
        const chart = echarts.init(el);
        const handleResize = () => {
            if (!chart.isDisposed()) {
                chart.resize();
            }
        };
        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(el);
        }
        window.addEventListener('resize', handleResize);

        const axisLabels = dayKeys.map((k) => {
            try {
                return DateTime.fromISO(k, {zone: 'utc'}).toLocaleString({
                    month: 'short',
                    day: 'numeric',
                });
            } catch {
                return k.length >= 10 ? k.slice(5) : k;
            }
        });
        const dayCount = axisLabels.length;
        const labelInterval = Math.max(0, Math.ceil(dayCount / TARGET_AXIS_LABELS) - 1);
        const rotate = dayCount > 10 ? 28 : 0;
        const axisLabel = {
            rotate,
            interval: labelInterval,
            hideOverlap: true,
        };

        const tooltipAxis = {
            trigger: 'axis' as const,
            axisPointer: {type: 'line' as const},
            formatter: (params: Array<{dataIndex: number; marker: string; seriesName: string; value: number}>) => {
                if (!params?.length) {
                    return '';
                }
                const idx = params[0].dataIndex;
                const title = tooltipDayTitle(dayKeys[idx] ?? '');
                const lines = params.map(
                    (p) => `${p.marker} ${p.seriesName}: <strong>${p.value}</strong>`,
                );
                return `${title}<br/>${lines.join('<br/>')}`;
            },
        };

        chart.setOption(
            {
                textStyle: {fontFamily: '"Rubik", sans-serif', fontSize: 12},
                tooltip: tooltipAxis,
                legend: {
                    type: 'scroll',
                    bottom: 0,
                    data: timelineSeries.map((s) => timelineSeriesName(s)),
                },
                grid: {left: 44, right: 12, top: 12, bottom: rotate ? 72 : 48},
                xAxis: {
                    type: 'category',
                    data: axisLabels,
                    axisLabel,
                },
                yAxis: {type: 'value', minInterval: 1},
                series: timelineSeries.map((s) => ({
                    name: timelineSeriesName(s),
                    type: 'line' as const,
                    smooth: true,
                    showSymbol: dayCount <= 24,
                    data: s.data,
                })),
            },
            true,
        );

        return () => {
            window.removeEventListener('resize', handleResize);
            if (resizeObserver && el) {
                resizeObserver.unobserve(el);
            }
            chart.dispose();
        };
    }, [dayKeys, timelineSeries, showLine]);

    if (!showPie && !showLine) {
        return null;
    }

    return (
        <Row className="g-2 mb-3">
            {showPie ? (
                <Col md={5}>
                    <div className="small text-muted mb-1">{i18n.t('ub_chart_storyboard_access_pie.text')}</div>
                    <div ref={pieRef} style={{width: '100%', height: CHART_HEIGHT_PX}} />
                </Col>
            ) : null}
            {showLine ? (
                <Col md={showPie ? 7 : 12}>
                    <div className="small text-muted mb-1">
                        {i18n.t('ub_chart_storyboard_access_timeline.text')}
                    </div>
                    <div ref={lineRef} style={{width: '100%', height: CHART_HEIGHT_PX}} />
                </Col>
            ) : null}
        </Row>
    );
};

export default UserAuditStoryboardAccessCharts;
